import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit3, Trash2, Calendar, MapPin, ChevronRight, X, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmModal";

function EventForm({ existing, onSave, onCancel }) {
  const isEdit = !!existing;
  const [f, setF] = useState({
    name: existing?.name || "",
    date: existing?.date || "",
    end_date: existing?.end_date || existing?.date || "",
    location: existing?.location || "",
    client: existing?.client || "",
    start_time: existing?.start_time || "08:00",
    end_time: existing?.end_time || "18:00",
    game_duration: existing?.game_duration || 45,
    surfaces: existing?.surfaces || 2,
    break_between: existing?.break_between ?? 5,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.name || !f.date) {
      toast.error("Nom et date de début requis");
      return;
    }
    if (f.end_date && f.end_date < f.date) {
      toast.error("La date de fin doit être après (ou égale à) la date de début");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...f,
        end_date: f.end_date || f.date,
        game_duration: parseInt(f.game_duration),
        break_between: parseInt(f.break_between),
        surfaces: parseInt(f.surfaces),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-8 max-w-3xl w-full shadow-2xl my-8" data-testid="event-form-modal">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="label mb-1">{isEdit ? "Modifier" : "Nouveau mandat"}</div>
            <h2 className="font-serif text-4xl leading-tight">{isEdit ? "Modifier l'événement" : "Créer un événement"}</h2>
          </div>
          <button onClick={onCancel} className="text-[var(--muted)] hover:text-black" data-testid="event-form-close-btn">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Nom du tournoi *">
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-box" data-testid="event-name-input" />
          </Field>
          <Field label="Date début *">
            <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value, end_date: f.end_date && f.end_date >= e.target.value ? f.end_date : e.target.value })} className="input-box" data-testid="event-date-input" />
          </Field>
          <Field label="Date fin">
            <input type="date" value={f.end_date} min={f.date || undefined} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="input-box" data-testid="event-end-date-input" />
          </Field>
          <Field label="Lieu">
            <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="input-box" />
          </Field>
          <Field label="Client">
            <input value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })} className="input-box" />
          </Field>
          <Field label="Heure début">
            <input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className="input-box" />
          </Field>
          <Field label="Heure fin">
            <input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className="input-box" />
          </Field>
          <Field label="Nb. surfaces">
            <input type="number" min="1" value={f.surfaces} onChange={(e) => setF({ ...f, surfaces: e.target.value })} className="input-box" disabled={isEdit} />
          </Field>
          <Field label="Durée partie (min)">
            <input type="number" min="1" value={f.game_duration} onChange={(e) => setF({ ...f, game_duration: e.target.value })} className="input-box" />
          </Field>
          <Field label="Pause défaut (min)">
            <input type="number" min="0" value={f.break_between} onChange={(e) => setF({ ...f, break_between: e.target.value })} className="input-box" />
          </Field>
        </div>

        {isEdit ? (
          <p className="mt-6 text-xs text-[var(--muted)] border-l-4 border-black pl-3 italic">
            ℹ La modification ne change pas l'horaire existant. Pour modifier les parties, ouvre l'événement.
          </p>
        ) : null}

        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onCancel} className="btn-secondary" data-testid="event-form-cancel-btn">Annuler</button>
          <button onClick={submit} disabled={saving} className="btn-primary" data-testid="event-form-save-btn">
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer l'horaire"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function EventsList() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const fetchEvents = async (opts = {}) => {
    try {
      const withArchived = opts.showArchived ?? showArchived;
      const { data } = await api.get(`/events${withArchived ? "?include_archived=1" : ""}`);
      setEvents(data.events);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleArchived = async () => {
    const next = !showArchived;
    setShowArchived(next);
    setLoading(true);
    await fetchEvents({ showArchived: next });
  };

  const handleCreate = async (form) => {
    try {
      const { data } = await api.post("/events", form);
      setEvents([data.event, ...events]);
      setShowForm(false);
      toast.success("Événement créé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleEdit = async (form) => {
    try {
      const { data } = await api.patch(`/events/${editing.id}`, form);
      setEvents(events.map((e) => (e.id === editing.id ? data.event : e)));
      setEditing(null);
      toast.success("Événement mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (ev, e) => {
    e?.stopPropagation();
    const ok = await confirm.ask({
      title: "Supprimer cet événement?",
      message: `"${ev.name}" et toutes ses parties seront supprimés. Action irréversible.`,
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/events/${ev.id}`);
      setEvents(events.filter((x) => x.id !== ev.id));
      toast.success("Événement supprimé");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    }
  };

  return (
    <div data-testid="events-list-page">
      {confirm.modal}
      {showForm && <EventForm onSave={handleCreate} onCancel={() => setShowForm(false)} />}
      {editing && <EventForm existing={editing} onSave={handleEdit} onCancel={() => setEditing(null)} />}

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <div className="section-number">01 / Événements</div>
          <h1 className="h1-big">Tes mandats.</h1>
          <p className="text-sm text-[var(--muted)] mt-3 max-w-xl leading-relaxed">
            Crée un événement, génère son horaire, assigne les officiels, puis ferme-le pour générer paies et facture.
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="new-event-btn">
          <Plus size={14} strokeWidth={2.5} />
          Nouvel événement
        </button>
      </div>

      <div className="flex justify-end mb-4">
        <label className="inline-flex items-center gap-2 cursor-pointer font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black" data-testid="toggle-archived">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={toggleArchived}
            className="w-3.5 h-3.5"
            data-testid="toggle-archived-input"
          />
          Afficher les événements archivés (+3 jours après la fin)
        </label>
      </div>

      {loading ? (
        <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse">Chargement…</p>
      ) : events.length === 0 ? (
        <div className="border-2 border-dashed border-[var(--border-strong)] p-16 text-center">
          <p className="font-serif text-3xl italic mb-2">Aucun événement.</p>
          <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)]">Crée ton premier mandat pour démarrer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {events.map((ev, i) => {
            const playable = (ev.games || []).filter((g) => !g.is_break);
            const total = playable.length;
            const assigned = playable.filter((g) => g.referee1_id || g.referee2_id || g.scorer_id).length;
            const pct = total > 0 ? Math.round((assigned / total) * 100) : 0;
            return (
              <div
                key={ev.id}
                className="card-flat cursor-pointer card-hover"
                onClick={() => navigate(`/evenements/${ev.id}`)}
                data-testid={`event-card-${ev.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">#{String(i + 1).padStart(3, "0")}</span>
                  {ev.closed ? (
                    <span className="badge-dark inline-flex items-center gap-1">
                      <CheckSquare size={10} strokeWidth={3} /> Fermé
                    </span>
                  ) : (
                    <span className="badge-dashed">Ouvert</span>
                  )}
                </div>
                <h3 className="font-serif text-3xl leading-tight">{ev.name}</h3>
                <div className="space-y-1 font-mono text-xs text-[var(--text)]">
                  <div className="flex items-center gap-2">
                    <Calendar size={12} strokeWidth={2.5} />
                    {ev.date || "Date non définie"} · {ev.start_time}→{ev.end_time}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={12} strokeWidth={2.5} />
                    {ev.location || "—"}
                  </div>
                </div>
                <div>
                  <div className="flex justify-between font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mb-1.5">
                    <span>Assignations</span>
                    <span className="tabular">
                      {assigned}/{total} · {pct}%
                    </span>
                  </div>
                  <div className="progress-track w-full">
                    <div className="progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(ev);
                      }}
                      className="p-1.5 border border-transparent hover:border-black"
                      title="Modifier"
                      data-testid={`edit-event-${ev.id}`}
                    >
                      <Edit3 size={14} strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(ev, e)}
                      className="p-1.5 border border-transparent hover:border-black hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)]"
                      title="Supprimer"
                      data-testid={`delete-event-${ev.id}`}
                    >
                      <Trash2 size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] flex items-center gap-1">
                    Ouvrir <ChevronRight size={12} strokeWidth={2.5} />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
